/**
 * Dynamic Lists (segments) — shared DTO contract + the SAFE filter allowlist.
 *
 * The filter "field" and "operator" allowlists live here so the API compiler
 * and the web rule-builder agree on exactly what is expressible. The API is the
 * security boundary: it re-validates every field/operator/value against these
 * allowlists at compile time and ALWAYS binds values as SQL parameters.
 */

// ----------------------------------------------------------------------------
// Filter definition (over-the-wire, mirrors lists.definition jsonb)
// ----------------------------------------------------------------------------

export type ListCombinator = 'and' | 'or'

export type ListRuleValue = string | number | boolean | Array<string | number> | null

export interface ListRule {
  field: string
  operator: ListOperator
  value?: ListRuleValue
}

export interface ListRuleGroup {
  combinator: ListCombinator
  rules: Array<ListRule | ListRuleGroup>
}

export interface ListDefinition {
  combinator: ListCombinator
  rules: Array<ListRule | ListRuleGroup>
}

export function isRuleGroup(node: ListRule | ListRuleGroup): node is ListRuleGroup {
  return (node as ListRuleGroup).combinator !== undefined
}

// ----------------------------------------------------------------------------
// Operators
// ----------------------------------------------------------------------------

export const LIST_OPERATORS = [
  'eq',
  'neq',
  'contains',
  'starts_with',
  'gt',
  'gte',
  'lt',
  'lte',
  'before',
  'after',
  'in',
  'not_in',
  'is_set',
  'is_not_set',
  'is_true',
  'is_false',
] as const

export type ListOperator = (typeof LIST_OPERATORS)[number]

/** The kind of value an operator/field expects, used for input rendering + validation. */
export type ListValueKind = 'text' | 'number' | 'date' | 'boolean' | 'enum' | 'none' | 'list'

/** Which operators apply to a given field data type. */
export type ListFieldType = 'text' | 'number' | 'date' | 'boolean' | 'enum'

export const OPERATORS_BY_FIELD_TYPE: Record<ListFieldType, ListOperator[]> = {
  text: ['eq', 'neq', 'contains', 'starts_with', 'in', 'not_in', 'is_set', 'is_not_set'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_set', 'is_not_set'],
  date: ['before', 'after', 'is_set', 'is_not_set'],
  boolean: ['is_true', 'is_false'],
  enum: ['eq', 'neq', 'in', 'not_in', 'is_set', 'is_not_set'],
}

/** The `tags` field (a text[]) supports membership-style operators only. */
export const TAGS_FIELD_KEY = 'tags'
export const TAGS_OPERATORS: ListOperator[] = ['eq', 'neq', 'in', 'not_in', 'is_set', 'is_not_set']

/** Operators valid for a given field key (handles the `tags` special case). */
export function operatorsForField(fieldKey: string, type: ListFieldType): ListOperator[] {
  if (fieldKey === TAGS_FIELD_KEY) return TAGS_OPERATORS
  return OPERATORS_BY_FIELD_TYPE[type]
}

/** Operators that take no value (unary). */
export const UNARY_OPERATORS: ReadonlySet<ListOperator> = new Set<ListOperator>([
  'is_set',
  'is_not_set',
  'is_true',
  'is_false',
])

/** Operators whose value is an array. */
export const ARRAY_OPERATORS: ReadonlySet<ListOperator> = new Set<ListOperator>(['in', 'not_in'])

// ----------------------------------------------------------------------------
// Fields — the allowlist of person attributes a rule may reference.
// Custom-field references use the synthetic key `custom:<custom_field_id>` and
// are resolved against the org's custom_fields at compile time.
// ----------------------------------------------------------------------------

export interface ListFieldDef {
  /** Stable field key used in stored rules. */
  key: string
  label: string
  type: ListFieldType
  /** For enum fields: the allowed option values (also enforced server-side). */
  options?: { value: string; label: string }[]
  group: string
}

export const CUSTOM_FIELD_PREFIX = 'custom:'

export const PERSON_LIST_FIELDS: ListFieldDef[] = [
  { key: 'first_name', label: 'First name', type: 'text', group: 'Identity' },
  { key: 'last_name', label: 'Last name', type: 'text', group: 'Identity' },
  { key: 'nickname', label: 'Nickname', type: 'text', group: 'Identity' },
  { key: 'gender', label: 'Gender', type: 'text', group: 'Identity' },
  {
    key: 'marital_status',
    label: 'Marital status',
    type: 'enum',
    group: 'Identity',
    options: [
      { value: 'single', label: 'Single' },
      { value: 'married', label: 'Married' },
      { value: 'widowed', label: 'Widowed' },
      { value: 'divorced', label: 'Divorced' },
      { value: 'separated', label: 'Separated' },
    ],
  },
  {
    key: 'membership_status',
    label: 'Membership status',
    type: 'enum',
    group: 'Membership',
    options: [
      { value: 'visitor', label: 'Visitor' },
      { value: 'regular', label: 'Regular' },
      { value: 'member', label: 'Member' },
      { value: 'inactive', label: 'Inactive' },
    ],
  },
  { key: 'is_minor', label: 'Is a minor', type: 'boolean', group: 'Membership' },
  { key: 'membership_date', label: 'Membership date', type: 'date', group: 'Membership' },
  { key: 'birthdate', label: 'Birthdate', type: 'date', group: 'Dates' },
  { key: 'anniversary', label: 'Anniversary', type: 'date', group: 'Dates' },
  { key: 'primary_email', label: 'Email', type: 'text', group: 'Contact' },
  { key: 'primary_phone', label: 'Phone', type: 'text', group: 'Contact' },
  { key: 'primary_site_id', label: 'Campus / site (id)', type: 'enum', group: 'Contact' },
  {
    key: 'expected_graduation_year',
    label: 'Expected graduation year',
    type: 'number',
    group: 'Dates',
  },
  { key: 'tags', label: 'Tag', type: 'text', group: 'Membership' },
]

export const PERSON_LIST_FIELD_BY_KEY: Record<string, ListFieldDef> = Object.fromEntries(
  PERSON_LIST_FIELDS.map((f) => [f.key, f]),
)

// ----------------------------------------------------------------------------
// DTOs
// ----------------------------------------------------------------------------

export type ListKind = 'dynamic' | 'static'

export interface ListSummary {
  id: string
  name: string
  slug: string
  description: string | null
  kind: ListKind
  cachedCount: number | null
  lastEvaluatedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ListDetail extends ListSummary {
  definition: ListDefinition
  ownerUserId: string | null
}

export interface NewListInput {
  name: string
  description?: string | null
  kind?: ListKind
  definition?: ListDefinition
}

export interface UpdateListInput {
  name?: string
  description?: string | null
  definition?: ListDefinition
}

/** A person row in a preview/sample or materialized member set. */
export interface ListPerson {
  id: string
  firstName: string
  lastName: string
  email: string | null
  status: string
}

export interface ListPreviewResult {
  count: number
  sample: ListPerson[]
}

export interface ListMaterializeResult {
  count: number
  added: number
  removed: number
  materializedAt: string
}
